import React, { useContext } from 'react';
import {
  Typography,
  Divider,
  Avatar,
  ListItem,
  ListItemAvatar,
  Box,
  Badge,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { format, formatDistanceToNowStrict } from 'date-fns';
import { ChatContext } from '@/context/ChatContext';
import { useAuth } from '@/context/AuthContext';
import ChatFileAttachment from './ChatFileAttachment';
import { useTranslation } from '@/hooks/useTranslation';
import Zoom from '@mui/material/Zoom';

const ChatContent: React.FC = () => {
  const { selectedChat }: any = useContext(ChatContext);
  const { user } = useAuth();
  const { t } = useTranslation();

  const FILE_SERVICE_STATUSES = [
    'processing',
    'completed',
    'closed',
    'cancelled',
    'declined',
  ] as const;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'open':
        return 'success';
      case 'processing':
        return 'warning';
      case 'closed':
        return 'default';
      case 'cancelled':
      case 'declined':
        return 'error';
      default:
        return 'default';
    }
  };

  const statusLabel =
    FILE_SERVICE_STATUSES.includes(
      selectedChat?.status as (typeof FILE_SERVICE_STATUSES)[number],
    )
      ? t(`fileService.status.${selectedChat.status}`)
      : selectedChat?.status ?? '';

  // Ticket chats: id === 'other'. File service chats: fileId as id.
  const isFileServiceChat = selectedChat?.id !== 'other';

  // Left-side (other) sender label in message list: customer sees "Support", admin sees client name or company.
  const otherSenderLabel = (() => {
    if (user?.userType === 'customer') return t('chat.supportLabel');
    const c = selectedChat?.customer;
    if (!c) return selectedChat?.name ?? '';
    const isBusiness = c.customerTypeName?.toLowerCase()?.includes('business');
    if (isBusiness) return c.companyName ?? c.email ?? selectedChat?.name ?? '';
    const full = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim();
    return (full || c.email || selectedChat?.name) ?? '';
  })();

  // Right-side (me) sender label: customer sees "You", admin sees "Support".
  const meSenderLabel =
    user?.userType === 'customer' ? t('chat.you') : t('chat.supportLabel');

  return (
    <Box sx={{maxHeight: '55vh', overflow: 'auto', flexGrow: 1}}>
      {selectedChat ? (
        <Box>
          {/* ------------------------------------------- */}
          {/* Header Part */}
          {/* ------------------------------------------- */}
          <Box>
            <Box display="flex" alignItems="center" p={2}>
              <Box
                sx={{
                  display: { xs: 'block', md: 'block', lg: 'none' },
                  mr: '10px',
                }}
              >
                {/* <IconMenu2 stroke={1.5} onClick={toggleChatSidebar} /> */}
              </Box>
              <ListItem key={selectedChat.id} dense disableGutters>
                <ListItemAvatar>
                  <Badge
                    color={
                      selectedChat.status === 'online'
                        ? 'success'
                        : selectedChat.status === 'busy'
                          ? 'error'
                          : selectedChat.status === 'away'
                            ? 'warning'
                            : 'secondary'
                    }
                    variant="dot"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    overlap="circular"
                  >
                    {selectedChat.customer ? (
                      <Tooltip
                        placement="top"
                        slots={{
                          transition: Zoom,
                        }}
                        title={
                          <Box>
                            {selectedChat.customer.customerTypeName
                              ?.toLowerCase()
                              ?.includes('business') ? (
                              selectedChat.customer.companyName && (
                                <Typography variant="body2">
                                  <strong>
                                    {selectedChat.customer.companyName}
                                  </strong>
                                </Typography>
                              )
                            ) : (
                              <Typography variant="body2">
                                <strong>
                                  {selectedChat.customer.firstName ?? ''}{' '}
                                  {selectedChat.customer.lastName ?? ''}
                                </strong>
                              </Typography>
                            )}
                            <Typography variant="body2">
                              {selectedChat.customer.email ?? ''}
                            </Typography>
                            <Typography variant="body2">
                              Credits: {selectedChat.customer.creditsAmount ?? 0}
                            </Typography>
                          </Box>
                        }
                        arrow
                      >
                        <Avatar
                          alt={selectedChat.name}
                          src={selectedChat.thumb}
                          sx={{ width: 40, height: 40 }}
                        />
                      </Tooltip>
                    ) : (
                      <Avatar
                        alt={selectedChat.name}
                        src={selectedChat.thumb}
                        sx={{ width: 40, height: 40 }}
                      />
                    )}
                  </Badge>
                </ListItemAvatar>
                <Box
                  display="flex"
                  flexDirection="column"
                  ml={2}
                  mr={'auto'}
                  alignItems={'flex-start'}
                  justifyContent={'start'}
                >
                  {isFileServiceChat ? (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      maxWidth="100%"
                    >
                      <Typography
                        variant="h5"
                        noWrap
                        sx={{
                          maxWidth: 500,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {selectedChat.name}
                      </Typography>
                      <Tooltip title="Copy">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              // eslint-disable-next-line no-undef
                              navigator.clipboard.writeText(
                                selectedChat?.name ?? '',
                              )
                            }
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography variant="h5">{selectedChat.name}</Typography>
                  )}
                  <Chip
                    label={statusLabel}
                    size="small"
                    color={getStatusColor(selectedChat.status.toLowerCase())}
                  />
                </Box>
              </ListItem>
              {/*<Stack direction={'row'}>*/}
              {/*  <IconButton aria-label="phone">*/}
              {/*    <IconPhone stroke={1.5} />*/}
              {/*  </IconButton>*/}
              {/*  <IconButton aria-label="video">*/}
              {/*    <IconVideo stroke={1.5} />*/}
              {/*  </IconButton>*/}
              {/*  <IconButton aria-label="sidebar" onClick={() => setOpen(!open)}>*/}
              {/*    <IconDotsVertical stroke={1.5} />*/}
              {/*  </IconButton>*/}
              {/*</Stack>*/}
            </Box>
            <Divider />
          </Box>
          {/* ------------------------------------------- */}
          {/* Chat Content */}
          {/* ------------------------------------------- */}

          <Box display="flex">
            {/* ------------------------------------------- */}
            {/* Chat msges */}
            {/* ------------------------------------------- */}

            <Box width="100%">
              <Box
               sx={{flexGrow: 1, minHeight: '550px'}}
              >
                <Box p={3}>
                  {selectedChat.messages.map((chat: any) => {
                    return (
                      <Box key={chat.id + chat.createdAt}>
                        {selectedChat.id === chat.senderId ||
                        chat.senderId === 'other' ? (
                          <Box display="flex">
                            <ListItemAvatar>
                              <Avatar
                                alt={otherSenderLabel}
                                src={selectedChat.thumb}
                                sx={{ width: 40, height: 40 }}
                              />
                            </ListItemAvatar>
                            <Box>
                              {chat.createdAt ? (
                                <Tooltip
                                  title={format(
                                    new Date(chat.createdAt),
                                    'dd.MM.yyyy, HH:mm:ss',
                                  )}
                                  placement="top"
                                >
                                  <Typography
                                    variant="body2"
                                    color="grey.400"
                                    mb={1}
                                    component="span"
                                    sx={{ display: 'inline-block', cursor: 'help' }}
                                  >
                                    {otherSenderLabel},{' '}
                                    {formatDistanceToNowStrict(
                                      new Date(chat.createdAt),
                                      {
                                        addSuffix: false,
                                      },
                                    )}{' '}
                                    {t('chat.ago')}
                                  </Typography>
                                </Tooltip>
                              ) : null}
                              {chat.msg && chat.type !== 'image' ? (
                                <Box
                                  mb={2}
                                  sx={{
                                    p: 1,
                                    backgroundColor: 'grey.100',
                                    mr: 'auto',
                                    maxWidth: '320px',
                                  }}
                                >
                                  {chat.msg}
                                </Box>
                              ) : null}
                              {chat.type === 'image' ? (
                                <Box
                                  mb={1}
                                  sx={{
                                    overflow: 'hidden',
                                    lineHeight: '0px',
                                  }}
                                >
                                  <img
                                    src={chat.msg}
                                    alt="attach"
                                    width="150"
                                    height="150"
                                  />
                                </Box>
                              ) : null}
                              {chat.attachment &&
                              chat.attachment.length > 0 &&
                              chat.attachment[0].file ? (
                                <ChatFileAttachment
                                  fileUrl={chat.attachment[0].file}
                                  fileName={chat.attachment[0].fileOriginalName}
                                />
                              ) : null}
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            mb={1}
                            display="flex"
                            alignItems="flex-end"
                            flexDirection="row-reverse"
                          >
                            <Box
                              alignItems="flex-end"
                              display="flex"
                              flexDirection={'column'}
                            >
                              {chat.createdAt ? (
                                <Tooltip
                                  title={format(
                                    new Date(chat.createdAt),
                                    'dd.MM.yyyy, HH:mm:ss',
                                  )}
                                  placement="top"
                                >
                                  <Typography
                                    variant="body2"
                                    color="grey.400"
                                    mb={1}
                                    component="span"
                                    sx={{ display: 'inline-block', cursor: 'help' }}
                                  >
                                    {meSenderLabel},{' '}
                                    {formatDistanceToNowStrict(
                                      new Date(chat.createdAt),
                                      {
                                        addSuffix: false,
                                      }
                                    )}{' '}
                                    {t('chat.ago')}
                                  </Typography>
                                </Tooltip>
                              ) : null}
                              {chat.msg && chat.type !== 'image' ? (
                                <Box
                                  mb={1}
                                  sx={{
                                    p: 1,
                                    backgroundColor: 'primary.light',
                                    ml: 'auto',
                                    maxWidth: '320px',
                                  }}
                                >
                                  {chat.msg}
                                </Box>
                              ) : null}
                              {chat.type === 'image' ? (
                                <Box
                                  mb={1}
                                  sx={{ overflow: 'hidden', lineHeight: '0px' }}
                                >
                                  <img
                                    src={chat.msg}
                                    alt="attach"
                                    width="250"
                                    height="165"
                                  />
                                </Box>
                              ) : null}
                              {chat.attachment &&
                              chat.attachment.length > 0 &&
                              chat.attachment[0].file ? (
                                <ChatFileAttachment
                                  fileUrl={chat.attachment[0].file}
                                  fileName={chat.attachment[0].fileOriginalName}
                                />
                              ) : null}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* ------------------------------------------- */}
            {/* Chat right sidebar Content */}
            {/* ------------------------------------------- */}
            {/*{open ? (*/}
            {/*  <Box flexShrink={0}>*/}
            {/*    <ChatInsideSidebar*/}
            {/*      isInSidebar={lgUp ? open : !open}*/}
            {/*      chat={selectedChat}*/}
            {/*    />*/}
            {/*  </Box>*/}
            {/*) : (*/}
            {/*  ''*/}
            {/*)}*/}
          </Box>
        </Box>
      ) : (
        <Box display="flex" alignItems="center" p={2} pb={1} pt={1}>
          <Typography variant="h4">{t('chat.selectChat')}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default ChatContent;
